// Shell slots: components other slices provide (docs/api/frontend_slots.md) that the shell mounts.
// They are found with import.meta.glob (slot-modules.ts), so a slot file that does not exist yet simply renders the
// fallback instead of breaking the build. Extra extension point: src/features/<domain>/slots/<Name>.tsx
// for AppBanner (strips under the top bar) and CommunityBanner (strips under the community tabs).
import { Component, lazy, Suspense, type ComponentType, type LazyExoticComponent, type ReactElement, type ReactNode } from 'react'
import { isChunkLoadError } from '@/features/errors/chunk'
import type { Community } from '@/lib/community'
import { slotModules, type AnyComponent } from './slot-modules'

export type CommunitySlotProps = { slug: string; community: Community | null }
export type PopoverSlotProps = { children: ReactElement }

export type SlotProps = {
  ChatPopover: PopoverSlotProps
  NotificationsPopover: PopoverSlotProps
  LevelUpToast: Record<string, never>
  ShortcutsProvider: Record<string, never>
  BillingBanner: { slug: string }
  CheckoutSuccessWatcher: { slug: string }
  CommunitySidebar: CommunitySlotProps
  CommunityHeaderCard: CommunitySlotProps
  CommunityBanner: CommunitySlotProps
  AppBanner: Record<string, never>
}
export type SlotName = keyof SlotProps

const SLOT_FILES: Record<SlotName, string[]> = {
  ChatPopover: ['/src/features/chat/components/ChatPopover.tsx'],
  NotificationsPopover: ['/src/features/notifications/components/NotificationsPopover.tsx'],
  LevelUpToast: ['/src/features/notifications/components/LevelUpToast.tsx'],
  ShortcutsProvider: ['/src/features/shortcuts/ShortcutsProvider.tsx'],
  BillingBanner: ['/src/features/billing/components/BillingBanner.tsx'],
  CheckoutSuccessWatcher: ['/src/features/billing/components/CheckoutSuccessWatcher.tsx'],
  CommunitySidebar: ['/src/features/communities/components/CommunitySidebar.tsx'],
  CommunityHeaderCard: ['/src/features/communities/components/CommunityHeaderCard.tsx'],
  CommunityBanner: [],
  AppBanner: [],
}

const modules = slotModules
const cache = new Map<string, LazyExoticComponent<AnyComponent>>()

function slotFiles(name: SlotName): string[] {
  const known = SLOT_FILES[name].filter((path) => path in modules)
  const extra = Object.keys(modules)
    .filter((path) => path.endsWith(`/slots/${name}.tsx`))
    .sort()
  return [...known, ...extra]
}

function lazyFor(path: string): LazyExoticComponent<AnyComponent> {
  const hit = cache.get(path)
  if (hit) return hit
  const created = lazy(modules[path])
  cache.set(path, created)
  return created
}

/**
 * E2E-J10-02: a slot whose chunk cannot be downloaded (a phone that just lost its signal) shows its fallback until
 * the next page load (browsers keep a failed module import failed) instead of taking the whole page down to the
 * route error page and its reload. Real crashes inside a slot still reach the route error page and get reported.
 */
export class SlotBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown }
  static getDerivedStateFromError(error: unknown) {
    return { error }
  }
  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (!isChunkLoadError(error)) throw error
    return this.props.fallback
  }
}

/** Renders the first provider of a slot, or the fallback when none exists (or while it loads). */
export function SingleSlot<N extends SlotName>({ name, props, fallback = null }: { name: N; props: SlotProps[N]; fallback?: ReactNode }) {
  const [first] = slotFiles(name)
  if (!first) return <>{fallback}</>
  const C = lazyFor(first) as unknown as ComponentType<SlotProps[N]>
  return (
    <SlotBoundary fallback={fallback}>
      <Suspense fallback={fallback}>
        <C {...props} />
      </Suspense>
    </SlotBoundary>
  )
}

/** Renders every provider of a slot in path order (banners). */
export function MultiSlot<N extends SlotName>({ name, props }: { name: N; props: SlotProps[N] }) {
  const files = slotFiles(name)
  if (files.length === 0) return null
  return (
    <Suspense fallback={null}>
      {files.map((path) => {
        const C = lazyFor(path) as unknown as ComponentType<SlotProps[N]>
        return (
          <SlotBoundary key={path} fallback={null}>
            <C {...props} />
          </SlotBoundary>
        )
      })}
    </Suspense>
  )
}
